import { createContext, ReactNode, useContext } from 'react'
import {
  ActionRequest,
  AlertPhone,
  Asset,
  Aurora,
  CatalogProduct,
  Company,
  Device,
  Generator,
  Heater,
  Project,
  ProjectMembership,
  RuuviTag,
  User
} from '../types'
// DO NOT IMPORT FROM '../hooks' DIRECTLY, IT WILL CAUSE CIRCULAR DEPENDENCIES
import useAuth from '../hooks/useAuth'
import { CatalogProductStatus, ProjectMembershipRole, UserRole } from '../enums'
import { RequestableType } from '../enums/RequestableType'
import {
  isCompanyActionRequest,
  isDeviceActionRequest,
  isProjectActionRequest
} from '../utils/actionRequest'
import { is } from 'date-fns/locale'

enum Feature {
  VIEW_COMPANY_HEATERS = 'VIEW_COMPANY_HEATERS',
  CREATE_HEATER = 'CREATE_HEATER',
  INSTALL_HEATER = 'INSTALL_HEATER',
  VIEW_HEATER = 'VIEW_HEATER',
  EDIT_HEATER = 'EDIT_HEATER',
  DELETE_HEATER = 'DELETE_HEATER',
  VIEW_AURORAS = 'VIEW_AURORAS'
}

interface AuroraPolicy {
  canViewAuroras: (auroras?: Aurora[]) => boolean
  canViewAurora: (aurora: Aurora) => boolean
  canCreateAurora: () => boolean
  canEditAurora: (aurora: Aurora) => boolean
  canDeleteAurora: (aurora: Aurora) => boolean
}

export interface PolicyState extends AuroraPolicy {
  isSuperadmin: boolean
  isAdmin: boolean
  isUser: boolean
  isInstaller: boolean
  isManufacturer: boolean
  isGuest: boolean
  isCurrentUser: (user: User | number) => boolean
  isMemberOfCompany: (company: Company | number) => boolean
  isAdminOfCompany: (company: Company | number) => boolean
  isUserOfCompany: (company: Company | number) => boolean
  isInstallerOfCompany: (company: Company | number) => boolean
  isMemberOfProject: (project: Project | number) => boolean
  isOwnerOfProject: (project: Project | number) => boolean
  isAdminOfProject: (project: Project | number) => boolean
  isUserOfProject: (project: Project | number) => boolean

  canViewDevices: () => boolean
  canCreateDevice: () => boolean
  canViewDevice: (device: Device) => boolean
  canViewDeviceConfiguration: (device: Device) => boolean
  canEditDevice: (device: Device) => boolean
  canDeleteDevice: (device: Device) => boolean

  /**
   * Checks if the current user can view device's alert rules.
   */
  canViewDeviceAlertRules: (device: Device) => boolean

  /**
   * Checks if the current user can edit device's alert rules.
   */
  canEditDeviceAlertRules: (device: Device) => boolean

  canTroubleshootDevice: (device: Device) => boolean

  /**
   * Check if the current user can view the RuuviTags.
   */
  canViewRuuviTags: () => boolean

  /**
   * Check if the current user can create the RuuviTag.
   */
  canCreateRuuviTag: () => boolean

  /**
   * Check if the current user can view the RuuviTag.
   */
  canViewRuuviTag: (ruuviTag: RuuviTag) => boolean

  /**
   * Check if the current user can edit the RuuviTag.
   */
  canEditRuuviTag: (ruuviTag: RuuviTag) => boolean

  /**
   * Check if the current user can delete the RuuviTag.
   */
  canDeleteRuuviTag: (ruuviTag: RuuviTag) => boolean

  /**
   * Check if the current user can view RuuviTag's alert rules.
   */
  canViewRuuviTagAlertRules: (ruuviTag: RuuviTag) => boolean

  /**
   * Check if the current user can edit RuuviTag's alert rules.
   */
  canEditRuuviTagAlertRules: (ruuviTag: RuuviTag) => boolean

  /**
   * Check if the current user can create a new asset for the company.
   */
  canCreateCompanyAsset: (company: Company) => boolean

  /**
   * Check if the current user can create a new asset.
   */
  canCreateProjectAsset: (project: Project) => boolean

  /**
   * Check if the current user can view the asset.
   */
  canViewAsset: (asset: Asset) => boolean

  /**
   * Check if the current user can edit the asset.
   */
  canEditAsset: (asset: Asset) => boolean

  /**
   * Check if the current user can delete the asset.
   */
  canDeleteAsset: (asset: Asset) => boolean

  canViewProjects: () => boolean
  canViewDeletedProjects: () => boolean
  canCreateProject: () => boolean
  canViewProject: (project: Project | number) => boolean
  canEditProject: (project: Project | number) => boolean
  canDeleteProject: (project: Project | number) => boolean
  canRestoreProject: (project: Project | number) => boolean
  canInviteUserToProject: (project: Project | number) => boolean

  /**
   * Checks if the current user can view the members of the project.
   */
  canViewProjectMembers: (project: Project | number) => boolean

  /**
   * Checks if the current user can view the member of the project.
   */
  canViewProjectMember: (project: Project | number, member: User | number) => boolean

  /**
   * Checks if the current user can edit the member of the project.
   */
  canEditProjectMember: (project: Project | number, member: User | number) => boolean

  /**
   * Checks if the current user can delete the member of the project.
   */
  canDeleteProjectMember: (project: Project | number, member: User | number) => boolean

  /**
   * Check if the current user can view the alert rules of the project.
   */
  canViewProjectAlertRules: (project: Project | number) => boolean

  /**
   * Check if the current user can edit the alert rules of the project.
   */
  canEditProjectAlertRules: (project: Project | number) => boolean

  canViewProjectActionRequests: (project: Project | number) => boolean

  canViewCompanies: () => boolean
  canViewDeletedCompanies: () => boolean
  canCreateCompany: () => boolean
  canViewCompany: (company: Company | number) => boolean
  canEditCompany: (company: Company | number) => boolean
  canDeleteCompany: (company: Company | number) => boolean
  canInviteCompanyUser: (company: Company | number) => boolean
  canViewUsers: () => boolean
  canViewDeletedUsers: () => boolean
  canCreateUser: () => boolean
  canViewUser: (user: User | number) => boolean
  canEditUser: (user: User | number) => boolean
  canEditUserRole: (user: User | number) => boolean
  canEditUserEmail: (user: User | number) => boolean
  canVerifyUserEmail: (user: User | number) => boolean
  canEditUserPhone: (user: User | number) => boolean
  canVerifyUserPhone: (user: User | number) => boolean
  canDeleteUser: (user: User | number) => boolean
  canRestoreUser: (user: User | number) => boolean

  /**
   * Checks if the current user can view the company of the user.
   */
  canViewUserCompany: (user: User | number) => boolean

  /**
   * Checks if the current user can view the projects of the user.
   */
  canViewUserProjects: (user: User | number) => boolean

  /**
   * Checks if the current user can view the requests of the user.
   */
  canViewUserRequests: (user: User | number) => boolean

  /**
   * Checks if the current user can view the user alert rules.
   */
  canViewUserAlertRules: (user: User | number) => boolean

  /**
   * Checks if the current user can edit user alert rules.
   */
  canEditUserAlertRules: (user: User | number) => boolean

  canViewAlertPhones: (project: Project | number) => boolean
  canViewAlertPhone: (alertPhone: AlertPhone) => boolean
  canCreateAlertPhone: (project: Project | number) => boolean
  canEditAlertPhone: (alertPhone: AlertPhone) => boolean
  canDeleteAlertPhone: (alertPhone: AlertPhone) => boolean

  canUploadPowerSupplyDocument: (device: Device) => boolean
  canEditPowerSupplyDocument?: (device: Device) => boolean

  canViewHeaters: () => boolean
  canViewCompanyHeaters: (company: Company) => boolean
  canCreateHeater: () => boolean
  canInstallHeater: () => boolean
  canViewHeater: (heater: Heater) => boolean
  canEditHeater: (heater: Heater) => boolean
  canDeleteHeater: (heater: Heater) => boolean

  // Generators
  canViewGenerators: () => boolean
  canViewGenerator: (generator: Generator) => boolean
  canCreateGenerator: () => boolean
  canInstallGenerator: () => boolean
  canEditGenerator: (generator: Generator) => boolean
  canEditGeneratorCustomData: (generator: Generator) => boolean
  canDeleteGenerator: (generator: Generator) => boolean

  // CatalogProuduct
  canViewCatalogProducts: () => boolean
  canCreateCatalogProduct: () => boolean
  canViewCatalogProduct: (catalogProduct: CatalogProduct) => boolean
  canEditCatalogProduct: (catalogProduct: CatalogProduct) => boolean
  canDeleteCatalogProduct: (catalogProduct: CatalogProduct) => boolean

  canUpdateActionRequestState: (actionRequest: ActionRequest) => boolean
}

export const PolicyContext = createContext<PolicyState>({
  isSuperadmin: false,
  isAdmin: false,
  isUser: false,
  isInstaller: false,
  isManufacturer: false,
  isGuest: false,
  isCurrentUser: () => false,
  isMemberOfCompany: () => false,
  isAdminOfCompany: () => false,
  isUserOfCompany: () => false,
  isInstallerOfCompany: () => false,
  isMemberOfProject: () => false,
  isOwnerOfProject: () => false,
  isAdminOfProject: () => false,
  isUserOfProject: () => false,
  canViewDevices: () => false,
  canCreateDevice: () => false,
  canViewDevice: () => false,
  canViewDeviceConfiguration: () => false,
  canEditDevice: () => false,
  canDeleteDevice: () => false,
  canViewDeviceAlertRules: () => false,
  canEditDeviceAlertRules: () => false,
  canViewRuuviTags: () => false,
  canCreateRuuviTag: () => false,
  canViewRuuviTag: () => false,
  canEditRuuviTag: () => false,
  canDeleteRuuviTag: () => false,
  canViewRuuviTagAlertRules: () => false,
  canEditRuuviTagAlertRules: () => false,
  canViewAsset: () => false,
  canCreateCompanyAsset: () => false,
  canCreateProjectAsset: () => false,
  canEditAsset: () => false,
  canDeleteAsset: () => false,
  canTroubleshootDevice: () => false,
  canViewProjects: () => false,
  canViewDeletedProjects: () => false,
  canCreateProject: () => false,
  canViewProject: () => false,
  canEditProject: () => false,
  canDeleteProject: () => false,
  canRestoreProject: () => false,
  canInviteUserToProject: () => false,
  canViewProjectMembers: () => false,
  canViewProjectMember: () => false,
  canEditProjectMember: () => false,
  canDeleteProjectMember: () => false,
  canViewProjectAlertRules: () => false,
  canEditProjectAlertRules: () => false,
  canViewProjectActionRequests: () => false,
  canViewCompanies: () => false,
  canViewDeletedCompanies: () => false,
  canCreateCompany: () => false,
  canViewCompany: () => false,
  canEditCompany: () => false,
  canDeleteCompany: () => false,
  canInviteCompanyUser: () => false,
  canViewUsers: () => false,
  canViewDeletedUsers: () => false,
  canCreateUser: () => false,
  canViewUser: () => false,
  canEditUser: () => false,
  canEditUserRole: () => false,
  canEditUserEmail: () => false,
  canVerifyUserEmail: () => false,
  canEditUserPhone: () => false,
  canVerifyUserPhone: () => false,
  canDeleteUser: () => false,
  canRestoreUser: () => false,
  canViewUserCompany: () => false,
  canViewUserProjects: () => false,
  canViewUserRequests: () => false,
  canViewUserAlertRules: () => false,
  canEditUserAlertRules: () => false,
  canViewAlertPhones: () => false,
  canViewAlertPhone: () => false,
  canCreateAlertPhone: () => false,
  canEditAlertPhone: () => false,
  canDeleteAlertPhone: () => false,
  canUploadPowerSupplyDocument: () => false,
  canEditPowerSupplyDocument: () => false,

  canViewHeaters: () => false,
  canViewCompanyHeaters: () => false,
  canCreateHeater: () => false,
  canInstallHeater: () => false,
  canViewHeater: () => false,
  canEditHeater: () => false,
  canDeleteHeater: () => false,
  canViewGenerators: () => false,
  canViewGenerator: () => false,
  canCreateGenerator: () => false,
  canInstallGenerator: () => false,
  canEditGenerator: () => false,
  canEditGeneratorCustomData: () => false,
  canDeleteGenerator: () => false,

  // CatalogProduct
  canViewCatalogProducts: () => false,
  canCreateCatalogProduct: () => false,
  canViewCatalogProduct: () => false,
  canEditCatalogProduct: () => false,
  canDeleteCatalogProduct: () => false,

  canUpdateActionRequestState: () => false,

  canViewAuroras: () => false,
  canViewAurora: () => false,
  canCreateAurora: () => false,
  canEditAurora: () => false,
  canDeleteAurora: () => false
})

export const usePolicy = () => {
  return useContext(PolicyContext)
}

const usePolicyHelpers = () => {
  const { currentUser } = useAuth()

  const hasRole = (role: UserRole) => currentUser?.role === role

  const isSuperadmin = hasRole(UserRole.SUPERADMIN)

  const isAdmin = hasRole(UserRole.ADMIN)

  const isUser = hasRole(UserRole.USER)

  const isInstaller = hasRole(UserRole.INSTALLER)

  const isManufacturer = hasRole(UserRole.MANUFACTURER)

  const isGuest = hasRole(UserRole.GUEST)

  const isCurrentUser = (user: User | number) => {
    const userId = typeof user === 'number' ? user : user.id
    return currentUser?.id === userId
  }

  const isMemberOfCompany = (company: Company | number | null): boolean => {
    if (company === null) {
      return false
    }

    const companyId = typeof company === 'number' ? company : company.id
    return (
      currentUser?.companyId === companyId ||
      currentUser?.company?.id === companyId ||
      Boolean(
        currentUser?.companyMemberships?.some((membership) => membership.companyId === companyId)
      )
    )
  }

  const hasCompanyRole = (company: Company | number, role: UserRole): boolean => {
    const companyId = typeof company === 'number' ? company : company.id
    const currentUserCompanyId = currentUser?.companyId || currentUser?.company?.id
    return (
      (currentUserCompanyId === companyId ||
        Boolean(
          currentUser?.companyMemberships?.some((membership) => membership.companyId === companyId)
        )) &&
      currentUser?.role === role
    )
  }

  const isAdminOfCompany = (company: Company | number | null) => {
    if (company === null) {
      return false
    }

    return hasCompanyRole(company, UserRole.ADMIN)
  }

  const isUserOfCompany = (company: Company | number | null) => {
    if (company === null) {
      return false
    }

    return hasCompanyRole(company, UserRole.USER)
  }

  const isInstallerOfCompany = (company: Company | number | null) => {
    if (company === null) {
      return false
    }

    return hasCompanyRole(company, UserRole.INSTALLER)
  }

  const isMemberOfProject = (project: Project | number) => {
    const projectId = typeof project === 'number' ? project : project.id
    return (
      currentUser?.projects?.some(
        (membership: ProjectMembership) =>
          membership.projectId === projectId || membership?.project?.id === projectId
      ) ?? false
    )
  }

  const hasProjectRole = (project: Project | number, role: ProjectMembershipRole): boolean => {
    const projectId = typeof project === 'number' ? project : project.id
    return (
      currentUser?.projects?.some(
        (membership: ProjectMembership) =>
          (membership.projectId === projectId || membership?.project?.id === projectId) &&
          membership.role === role
      ) ?? false
    )
  }

  const isOwnerOfProject = (project: Project | number) => {
    return hasProjectRole(project, ProjectMembershipRole.OWNER)
  }

  const isAdminOfProject = (project: Project | number) => {
    return hasProjectRole(project, ProjectMembershipRole.ADMIN)
  }

  const isUserOfProject = (project: Project | number) => {
    return hasProjectRole(project, ProjectMembershipRole.USER)
  }

  const isFeatureAllowed = (feature: Feature) => {
    const companies = import.meta.env?.[`VITE_APP_FEATURE_${feature}_ALLOWED_COMPANIES`]

    if (companies === '*') {
      return true
    }

    const allowedCompanies = JSON.parse(companies ?? '[]') as number[]

    if (allowedCompanies.some(isMemberOfCompany)) {
      return true
    }

    const projects = import.meta.env?.[`VITE_APP_FEATURE_${feature}_ALLOWED_PROJECTS`]

    if (projects === '*') {
      return true
    }

    const allowedProjects = JSON.parse(projects ?? '[]') as number[]
    if (allowedProjects.some(isMemberOfProject)) {
      return true
    }

    return false
  }

  return {
    isSuperadmin,
    isAdmin,
    isUser,
    isInstaller,
    isManufacturer,
    isGuest,
    isCurrentUser,
    isMemberOfCompany,
    isAdminOfCompany,
    isUserOfCompany,
    isInstallerOfCompany,
    isMemberOfProject,
    isOwnerOfProject,
    isAdminOfProject,
    isUserOfProject,
    isFeatureAllowed
  }
}

const withAuroraPolicies = (): AuroraPolicy => {
  const { isSuperadmin, isManufacturer, isMemberOfCompany, isMemberOfProject, isFeatureAllowed } =
    usePolicyHelpers()

  const canViewAurora = (aurora: Aurora) => {
    const isMemberOfOwner = aurora.companyId !== null && isMemberOfCompany(aurora.companyId)

    const isCompanyMember = Boolean(
      aurora?.device?.asset?.companyId && isMemberOfCompany(aurora.device.asset.companyId)
    )

    const isProjectMember = Boolean(
      aurora?.device?.asset?.projectId && isMemberOfProject(aurora.device.asset.projectId)
    )

    const isWithoutOwner = aurora.companyId === null

    const isWithoutDevice = aurora?.deviceId === null

    return (
      isSuperadmin ||
      isManufacturer ||
      isMemberOfOwner ||
      isCompanyMember ||
      isProjectMember ||
      isWithoutOwner ||
      isWithoutDevice
    )
  }

  const canViewAuroras = (auroras?: Aurora[]) => {
    if (auroras === undefined) {
      return isSuperadmin || isManufacturer || isFeatureAllowed(Feature.VIEW_AURORAS)
    } else {
      return auroras.every((aurora) => canViewAurora(aurora))
    }
  }

  const canCreateAurora = () => {
    return true
  }

  const canEditAurora = (aurora: Aurora) => {
    const isMemberOfOwner = aurora.companyId !== null && isMemberOfCompany(aurora.companyId)

    return isSuperadmin || isManufacturer || isMemberOfOwner
  }

  const canDeleteAurora = (aurora: Aurora) => {
    const isMemberOfOwner = aurora.companyId !== null && isMemberOfCompany(aurora.companyId)

    return isSuperadmin || isManufacturer || isMemberOfOwner
  }

  return {
    canViewAuroras,
    canViewAurora,
    canCreateAurora,
    canEditAurora,
    canDeleteAurora
  }
}

export interface PolicyProviderProps {
  children: ReactNode
}

export const PolicyProvider = ({ children }: PolicyProviderProps) => {
  const {
    isSuperadmin,
    isAdmin,
    isUser,
    isInstaller,
    isManufacturer,
    isGuest,
    isCurrentUser,
    isMemberOfCompany,
    isAdminOfCompany,
    isUserOfCompany,
    isInstallerOfCompany,
    isMemberOfProject,
    isOwnerOfProject,
    isAdminOfProject,
    isUserOfProject
  } = usePolicyHelpers()

  const canViewDevices = () => {
    return isSuperadmin || isManufacturer
  }

  const canCreateDevice = () => {
    return isSuperadmin || isManufacturer
  }

  const canViewDevice = (device: Device) => {
    const isCompanyMember = device?.asset?.companyId
      ? isMemberOfCompany(device.asset.companyId)
      : false

    const isProjectMember = device?.asset?.projectId
      ? isMemberOfProject(device.asset.projectId)
      : false

    return isSuperadmin || isInstaller || isManufacturer || isCompanyMember || isProjectMember
  }

  const canViewDeviceConfiguration = (device: Device) => {
    const deviceBelongsToCompanyThatUserIsAdminOf = device?.asset?.companyId
      ? isAdminOfCompany(device.asset.companyId)
      : false

    const deviceBelongsToProjectThatUserIsOwnerOrAdminOf = device?.asset?.projectId
      ? isOwnerOfProject(device.asset.projectId) || isAdminOfProject(device.asset.projectId)
      : false

    return (
      isSuperadmin ||
      isInstaller ||
      isManufacturer ||
      deviceBelongsToCompanyThatUserIsAdminOf ||
      deviceBelongsToProjectThatUserIsOwnerOrAdminOf
    )
  }

  const canEditDevice = (device: Device) => {
    const deviceBelongsToCompanyThatUserIsAdminOf = device?.asset?.companyId
      ? isAdminOfCompany(device.asset.companyId)
      : false

    const deviceBelongsToProjectThatUserIsOwnerOrAdminOf = device?.asset?.projectId
      ? isOwnerOfProject(device.asset.projectId) || isAdminOfProject(device.asset.projectId)
      : false

    return (
      isSuperadmin ||
      isInstaller ||
      isManufacturer ||
      deviceBelongsToCompanyThatUserIsAdminOf ||
      deviceBelongsToProjectThatUserIsOwnerOrAdminOf
    )
  }

  const canTroubleshootDevice = (_device: Device) => {
    return isSuperadmin
  }

  const canDeleteDevice = (_device: Device) => {
    return isSuperadmin || isManufacturer
  }

  const canViewDeviceAlertRules = (device: Device) => {
    const isAdminOfAssetCompany = device?.asset?.companyId
      ? isAdminOfCompany(device.asset.companyId)
      : false

    const isMemberOfAssetProject = device?.asset?.projectId
      ? isMemberOfProject(device.asset.projectId)
      : false

    return isSuperadmin || isAdminOfAssetCompany || isMemberOfAssetProject
  }

  const canEditDeviceAlertRules = (device: Device) => {
    const isAdminOfAssetCompany = device?.asset?.companyId
      ? isAdminOfCompany(device.asset.companyId)
      : false

    const isOwnerOrAdminOfAssetProject = device?.asset?.projectId
      ? isOwnerOfProject(device.asset.projectId) || isAdminOfProject(device.asset.projectId)
      : false

    return isSuperadmin || isAdminOfAssetCompany || isOwnerOrAdminOfAssetProject
  }

  const canViewRuuviTags = () => {
    return isSuperadmin || isManufacturer
  }

  const canCreateRuuviTag = () => {
    return isSuperadmin || isManufacturer
  }

  const canViewRuuviTag = (ruuviTag: RuuviTag) => {
    const belongsToCompanyThatUserIsMemberOf = ruuviTag?.asset?.companyId
      ? isMemberOfCompany(ruuviTag.asset.companyId)
      : false

    const belongsToProjectThatUserIsMemberOf = ruuviTag?.asset?.projectId
      ? isMemberOfProject(ruuviTag.asset.projectId)
      : false

    return (
      isSuperadmin ||
      isInstaller ||
      isManufacturer ||
      belongsToCompanyThatUserIsMemberOf ||
      belongsToProjectThatUserIsMemberOf
    )
  }

  const canEditRuuviTag = (ruuviTag: RuuviTag) => {
    const belongsToCompanyThatUserIsMemberOf = ruuviTag?.asset?.companyId
      ? isMemberOfCompany(ruuviTag.asset.companyId)
      : false

    const belongsToProjectThatUserIsMemberOf = ruuviTag?.asset?.projectId
      ? isMemberOfProject(ruuviTag.asset.projectId)
      : false

    return (
      isSuperadmin ||
      isInstaller ||
      isManufacturer ||
      belongsToCompanyThatUserIsMemberOf ||
      belongsToProjectThatUserIsMemberOf
    )
  }

  const canDeleteRuuviTag = (_ruuviTag: RuuviTag) => {
    return isSuperadmin || isManufacturer
  }

  const canViewRuuviTagAlertRules = (ruuviTag: RuuviTag) => {
    const isAdminOfAssetCompany = ruuviTag?.asset?.companyId
      ? isAdminOfCompany(ruuviTag.asset.companyId)
      : false

    const isMemberOfAssetProject = ruuviTag?.asset?.projectId
      ? isMemberOfProject(ruuviTag.asset.projectId)
      : false

    return isSuperadmin || isAdminOfAssetCompany || isMemberOfAssetProject
  }

  const canEditRuuviTagAlertRules = (ruuviTag: RuuviTag) => {
    const isAdminOfAssetCompany = ruuviTag?.asset?.companyId
      ? isAdminOfCompany(ruuviTag.asset.companyId)
      : false

    const isOwnerOfAdminOfAssetProject = ruuviTag?.asset?.projectId
      ? isOwnerOfProject(ruuviTag.asset.projectId) || isAdminOfProject(ruuviTag.asset.projectId)
      : false

    return isSuperadmin || isAdminOfAssetCompany || isOwnerOfAdminOfAssetProject
  }

  const canViewAsset = (asset: Asset) => {
    const isAdminOfCompanyThatOwnsTheAsset =
      asset.companyId !== null && isAdminOfCompany(asset.companyId)

    const isMemberOfProjectThatOwnsTheAsset =
      asset.projectId !== null && isMemberOfProject(asset.projectId)

    return isSuperadmin || isAdminOfCompanyThatOwnsTheAsset || isMemberOfProjectThatOwnsTheAsset
  }

  const canCreateCompanyAsset = (company: Company) => {
    return Boolean(isSuperadmin || isAdminOfCompany(company))
  }

  const canCreateProjectAsset = (project: Project) => {
    const isAdminOfProjectCompany =
      project.companyId !== null && isAdminOfCompany(project.companyId)

    const isOwnerOrAdminOfProject = isOwnerOfProject(project) || isAdminOfProject(project)

    return Boolean(isSuperadmin || isAdminOfProjectCompany || isOwnerOrAdminOfProject)
  }

  const canEditAsset = (asset: Asset) => {
    const isAdminOfCompanyThatOwnsTheAsset =
      asset.companyId !== null && isAdminOfCompany(asset.companyId)

    const isOwnerOrAdminOfProjectThatOwnsTheAsset =
      asset.projectId !== null &&
      (isOwnerOfProject(asset.projectId) || isAdminOfProject(asset.projectId))

    return (
      isSuperadmin || isAdminOfCompanyThatOwnsTheAsset || isOwnerOrAdminOfProjectThatOwnsTheAsset
    )
  }

  const canDeleteAsset = (asset: Asset) => {
    const isAdminOfCompanyThatOwnsTheAsset =
      asset.companyId !== null && isAdminOfCompany(asset.companyId)

    const isOwnerOrAdminOfProjectThatOwnsTheAsset =
      asset.projectId !== null &&
      (isOwnerOfProject(asset.projectId) || isAdminOfProject(asset.projectId))

    return (
      isSuperadmin || isAdminOfCompanyThatOwnsTheAsset || isOwnerOrAdminOfProjectThatOwnsTheAsset
    )
  }

  const canViewProjects = () => {
    return isSuperadmin || isAdmin || isUser || isGuest
  }

  const canViewDeletedProjects = () => {
    return isSuperadmin || isAdmin || isUser
  }

  const canCreateProject = () => {
    return isSuperadmin || isAdmin || isUser
  }

  const canViewProject = (project: Project | number) => {
    const isMemberOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isMemberOfCompany(project.companyId)

    return isSuperadmin || isMemberOfCompanyThatOwnsTheProject || isMemberOfProject(project)
  }

  const canEditProject = (project: Project | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canDeleteProject = (project: Project | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canRestoreProject = (project: Project | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canViewProjectMembers = (project: Project | number) => {
    const isMemberOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isMemberOfCompany(project.companyId)

    return isSuperadmin || isMemberOfCompanyThatOwnsTheProject || isMemberOfProject(project)
  }

  const canViewProjectMember = (project: Project | number, _member: User | number) => {
    const isMemberOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isMemberOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isMemberOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canEditProjectMember = (project: Project | number, _member: User | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canDeleteProjectMember = (project: Project | number, _member: User | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canViewProjectAlertRules = (project: Project | number) => {
    const isMemberOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isMemberOfCompany(project.companyId)

    return isSuperadmin || isMemberOfCompanyThatOwnsTheProject || isMemberOfProject(project)
  }

  const canEditProjectAlertRules = (project: Project | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canViewProjectActionRequests = (project: Project | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canInviteUserToProject = (project: Project | number) => {
    const isAdminOfCompanyThatOwnsTheProject =
      typeof project !== 'number' &&
      project.companyId !== null &&
      isAdminOfCompany(project.companyId)

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheProject ||
      isOwnerOfProject(project) ||
      isAdminOfProject(project)
    )
  }

  const canViewCompanies = () => {
    return isSuperadmin
  }

  const canViewDeletedCompanies = () => {
    return isSuperadmin
  }

  const canCreateCompany = () => {
    return isSuperadmin || isGuest
  }

  const canViewCompany = (company: Company | number) => {
    return isSuperadmin || isMemberOfCompany(company)
  }

  const canEditCompany = (company: Company | number) => {
    return isSuperadmin || isAdminOfCompany(company)
  }

  const canDeleteCompany = (company: Company | number) => {
    return isSuperadmin || isAdminOfCompany(company)
  }

  const canInviteCompanyUser = (company: Company | number) => {
    return isSuperadmin || isAdminOfCompany(company)
  }

  const canViewUsers = () => {
    return isSuperadmin
  }

  const canViewDeletedUsers = () => {
    return isSuperadmin
  }

  const canCreateUser = () => {
    return isSuperadmin || isAdmin
  }

  const canViewUser = (user: User | number) => {
    const isCompanyAdminOfUserWhoIsNotSuperadmin =
      typeof user !== 'number' &&
      user?.companyId &&
      isAdminOfCompany(user?.companyId) &&
      user?.role !== UserRole.SUPERADMIN

    return isSuperadmin || isCompanyAdminOfUserWhoIsNotSuperadmin || isCurrentUser(user)
  }

  const canEditUser = (user: User | number) => {
    const isCompanyAdminOfUserWhoIsNotSuperadminOrManufacturer = Boolean(
      typeof user !== 'number' &&
      user?.companyId &&
      isAdminOfCompany(user?.companyId) &&
      user?.role !== UserRole.SUPERADMIN &&
      user?.role !== UserRole.MANUFACTURER
    )

    return (
      isSuperadmin || isCompanyAdminOfUserWhoIsNotSuperadminOrManufacturer || isCurrentUser(user)
    )
  }

  const canEditUserRole = (user: User | number) => {
    const isCompanyAdminOfUserWhoIsNotSuperadminOrManufacturer = Boolean(
      typeof user !== 'number' &&
      user?.companyId &&
      isAdminOfCompany(user?.companyId) &&
      user?.role !== UserRole.SUPERADMIN &&
      user?.role !== UserRole.MANUFACTURER
    )

    return isSuperadmin || isCompanyAdminOfUserWhoIsNotSuperadminOrManufacturer
  }

  const canEditUserEmail = (_user: User | number) => {
    return isSuperadmin
  }

  const canVerifyUserEmail = (user: User | number) => {
    return isCurrentUser(user)
  }

  const canEditUserPhone = (user: User | number) => {
    return isSuperadmin || isCurrentUser(user)
  }

  const canVerifyUserPhone = (user: User | number) => {
    return isCurrentUser(user)
  }

  const canDeleteUser = (user: User | number) => {
    const isCompanyAdminOfUserWhoIsNotSuperadminOrManufacturer =
      typeof user !== 'number' &&
      user?.companyId &&
      isAdminOfCompany(user?.companyId) &&
      user?.role !== UserRole.SUPERADMIN &&
      user?.role !== UserRole.MANUFACTURER

    return (
      isSuperadmin || isCompanyAdminOfUserWhoIsNotSuperadminOrManufacturer || isCurrentUser(user)
    )
  }

  const canRestoreUser = (_user: User | number) => {
    return isSuperadmin
  }

  const canViewUserCompany = (user: User | number) => {
    const isMemberOfCompanyThatUserBelongsTo =
      typeof user !== 'number' &&
      ((user?.companyId && isMemberOfCompany(user.companyId)) ||
        (user?.company && isMemberOfCompany(user.company)))

    return isSuperadmin || isMemberOfCompanyThatUserBelongsTo || isCurrentUser(user)
  }

  const canViewUserProjects = (user: User | number) => {
    const isAdminOfCompanyThatUserBelongsTo =
      typeof user !== 'number' &&
      ((user?.companyId && isAdminOfCompany(user.companyId)) ||
        (user?.company && isAdminOfCompany(user.company)))

    return isSuperadmin || isAdminOfCompanyThatUserBelongsTo || isCurrentUser(user)
  }

  const canViewUserRequests = (user: User | number) => {
    return isSuperadmin || isCurrentUser(user)
  }

  const canViewUserAlertRules = (user: User | number) => {
    const isCompanyAdmin =
      typeof user !== 'number' && user?.companyId && isAdminOfCompany(user.companyId)

    return isSuperadmin || isCompanyAdmin || isCurrentUser(user)
  }

  const canEditUserAlertRules = (user: User | number) => {
    return isSuperadmin || isCurrentUser(user)
  }

  const canViewAlertPhones = (project: Project | number) => {
    return isSuperadmin || isOwnerOfProject(project) || isAdminOfProject(project)
  }

  const canCreateAlertPhone = (project: Project | number) => {
    return isSuperadmin || isOwnerOfProject(project) || isAdminOfProject(project)
  }

  const canViewAlertPhone = (alertPhone: AlertPhone) => {
    const isAdminOfCompanyThatOwnsTheAlertPhone = Boolean(
      alertPhone?.project?.companyId && isAdminOfCompany(alertPhone.project.companyId)
    )

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheAlertPhone ||
      isOwnerOfProject(alertPhone.projectId) ||
      isAdminOfProject(alertPhone.projectId)
    )
  }

  const canEditAlertPhone = (alertPhone: AlertPhone) => {
    const isAdminOfCompanyThatOwnsTheAlertPhone = Boolean(
      alertPhone?.project?.companyId && isAdminOfCompany(alertPhone.project.companyId)
    )

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheAlertPhone ||
      isOwnerOfProject(alertPhone.projectId) ||
      isAdminOfProject(alertPhone.projectId)
    )
  }

  const canDeleteAlertPhone = (alertPhone: AlertPhone) => {
    const isAdminOfCompanyThatOwnsTheAlertPhone = Boolean(
      alertPhone?.project?.companyId && isAdminOfCompany(alertPhone.project.companyId)
    )

    return (
      isSuperadmin ||
      isAdminOfCompanyThatOwnsTheAlertPhone ||
      isOwnerOfProject(alertPhone.projectId) ||
      isAdminOfProject(alertPhone.projectId)
    )
  }

  const canUploadPowerSupplyDocument = (device: Device) => {
    return (
      isSuperadmin ||
      Boolean(device?.asset?.companyId && isAdminOfCompany(device.asset.companyId)) ||
      Boolean(device.asset?.companyId && isUserOfCompany(device.asset.companyId))
    )
  }

  const canEditPowerSupplyDocument = (device: Device) => {
    return (
      isSuperadmin ||
      Boolean(device?.asset?.companyId && isAdminOfCompany(device.asset.companyId)) ||
      Boolean(device.asset?.companyId && isUserOfCompany(device.asset.companyId))
    )
  }

  const isFeatureAllowed = (feature: Feature) => {
    const value = import.meta.env?.[`VITE_APP_FEATURE_${feature}_ALLOWED_COMPANIES`]

    if (value === '*') {
      return true
    }

    const allowedCompanies = JSON.parse(value ?? '[]') as number[]

    return allowedCompanies.some(isMemberOfCompany)
  }

  const isCompanyHeater = (heater: Heater) => {
    return heater?.companyId !== null && isMemberOfCompany(heater.companyId)
  }

  const canViewHeaters = () => {
    return isSuperadmin || isManufacturer
  }

  const canViewCompanyHeaters = (_: Company) => {
    return isSuperadmin || isFeatureAllowed(Feature.VIEW_COMPANY_HEATERS)
  }

  const canCreateHeater = () => {
    return isSuperadmin || isManufacturer || isFeatureAllowed(Feature.CREATE_HEATER)
  }

  const canInstallHeater = () => {
    return isSuperadmin || isManufacturer || isFeatureAllowed(Feature.INSTALL_HEATER)
  }

  const canViewHeater = (heater: Heater) => {
    return (
      isSuperadmin ||
      isManufacturer ||
      (isFeatureAllowed(Feature.VIEW_HEATER) &&
        isCompanyHeater(heater) &&
        isMemberOfCompany(heater.companyId))
    )
  }

  const canEditHeater = (heater: Heater) => {
    return (
      isSuperadmin ||
      isManufacturer ||
      (isFeatureAllowed(Feature.EDIT_HEATER) &&
        isCompanyHeater(heater) &&
        isMemberOfCompany(heater.companyId))
    )
  }

  const canDeleteHeater = (heater: Heater) => {
    return (
      isSuperadmin ||
      isManufacturer ||
      (isFeatureAllowed(Feature.DELETE_HEATER) &&
        isCompanyHeater(heater) &&
        isAdminOfCompany(heater.companyId))
    )
  }

  const canUpdateActionRequestState = (actionRequest: ActionRequest) => {
    if (isSuperadmin) {
      return true
    }

    if (isCompanyActionRequest(actionRequest)) {
      return isAdminOfCompany(parseInt(actionRequest.requestableId))
    }

    if (isProjectActionRequest(actionRequest)) {
      return isAdminOfProject(parseInt(actionRequest.requestableId))
    }

    if (isDeviceActionRequest(actionRequest)) {
      return Boolean(
        (actionRequest.requestable?.asset?.companyId &&
          isAdminOfCompany(actionRequest.requestable.asset.companyId)) ||
        (actionRequest.requestable?.asset?.projectId &&
          isAdminOfProject(actionRequest.requestable.asset.projectId))
      )
    }

    return false
  }

  const canViewGenerators = () => {
    return isSuperadmin || isManufacturer
  }

  const canViewGenerator = (generator: Generator) => {
    const isCompanyMember = generator.companyId !== null && isMemberOfCompany(generator.companyId)

    const isProjectMember =
      generator?.device?.asset?.projectId !== undefined &&
      generator?.device?.asset?.projectId !== null &&
      isMemberOfProject(generator.device.asset.projectId)

    const isUnlinked =
      (generator?.deviceName !== undefined && generator?.deviceName !== null) ||
      (generator?.device?.asset?.projectId !== undefined &&
        generator?.device?.asset?.projectId === null)

    return isSuperadmin || isManufacturer || isCompanyMember || isProjectMember || isUnlinked
  }

  const canCreateGenerator = () => {
    return isSuperadmin || isManufacturer || isAdmin || isUser
  }

  const canInstallGenerator = () => {
    return isSuperadmin || isManufacturer || isAdmin || isUser
  }

  const canEditGenerator = (generator: Generator) => {
    const isCompanyMember = generator.companyId !== null && isMemberOfCompany(generator.companyId)

    return isSuperadmin || isManufacturer || isCompanyMember
  }

  const canEditGeneratorCustomData = (generator: Generator) => {
    const isCompanyMember = generator.companyId !== null

    const isProjectMember =
      generator?.device?.asset?.projectId !== undefined &&
      generator?.device?.asset?.projectId !== null &&
      isMemberOfProject(generator.device.asset.projectId)

    const isUnlinked =
      generator?.device?.asset?.projectId !== undefined &&
      generator?.device?.asset?.projectId === null

    return isSuperadmin || isManufacturer || isCompanyMember || isProjectMember || isUnlinked
  }

  const canDeleteGenerator = (generator: Generator) => {
    const isCompanyMember = generator.companyId !== null && isMemberOfCompany(generator.companyId)

    return isSuperadmin || isManufacturer || isCompanyMember
  }

  const canViewCatalogProducts = () => {
    return true
  }

  const canCreateCatalogProduct = () => {
    return true
  }

  const canViewCatalogProduct = (catalogProduct: CatalogProduct) => {
    return true
  }

  const canEditCatalogProduct = (catalogProduct: CatalogProduct) => {
    const isUnverified = catalogProduct.status === CatalogProductStatus.UNVERIFIED

    return isSuperadmin || isManufacturer || isUnverified
  }

  const canDeleteCatalogProduct = (catalogProduct: CatalogProduct) => {
    return isSuperadmin || isManufacturer
  }

  return (
    <PolicyContext.Provider
      value={{
        isSuperadmin,
        isAdmin,
        isUser,
        isInstaller,
        isManufacturer,
        isGuest,
        isCurrentUser,
        isMemberOfCompany,
        isAdminOfCompany,
        isUserOfCompany,
        isInstallerOfCompany,
        isMemberOfProject,
        isOwnerOfProject,
        isAdminOfProject,
        isUserOfProject,
        canViewDevices,
        canCreateDevice,
        canViewDevice,
        canViewDeviceConfiguration,
        canEditDevice,
        canTroubleshootDevice,
        canDeleteDevice,
        canViewDeviceAlertRules,
        canEditDeviceAlertRules,
        canViewRuuviTags,
        canCreateRuuviTag,
        canViewRuuviTag,
        canEditRuuviTag,
        canDeleteRuuviTag,
        canViewRuuviTagAlertRules,
        canEditRuuviTagAlertRules,
        canViewAsset,
        canCreateCompanyAsset,
        canCreateProjectAsset,
        canEditAsset,
        canDeleteAsset,
        canViewProjects,
        canViewDeletedProjects,
        canCreateProject,
        canViewProject,
        canEditProject,
        canDeleteProject,
        canRestoreProject,
        canInviteUserToProject,
        canViewProjectMembers,
        canViewProjectMember,
        canEditProjectMember,
        canDeleteProjectMember,
        canViewProjectAlertRules,
        canEditProjectAlertRules,
        canViewProjectActionRequests,
        canViewCompanies,
        canViewDeletedCompanies,
        canCreateCompany,
        canViewCompany,
        canEditCompany,
        canDeleteCompany,
        canInviteCompanyUser,
        canViewUsers,
        canViewDeletedUsers,
        canCreateUser,
        canViewUser,
        canEditUser,
        canEditUserRole,
        canEditUserEmail,
        canVerifyUserEmail,
        canEditUserPhone,
        canVerifyUserPhone,
        canDeleteUser,
        canRestoreUser,
        canViewUserCompany,
        canViewUserProjects,
        canViewUserRequests,
        canViewUserAlertRules,
        canEditUserAlertRules,
        canViewAlertPhones,
        canCreateAlertPhone,
        canViewAlertPhone,
        canEditAlertPhone,
        canDeleteAlertPhone,
        canUploadPowerSupplyDocument,
        canEditPowerSupplyDocument,

        canViewHeaters,
        canViewCompanyHeaters,
        canCreateHeater,
        canInstallHeater,
        canViewHeater,
        canEditHeater,
        canDeleteHeater,

        canViewGenerators,
        canViewGenerator,
        canInstallGenerator,
        canCreateGenerator,
        canEditGenerator,
        canEditGeneratorCustomData,
        canDeleteGenerator,

        canViewCatalogProducts,
        canCreateCatalogProduct,
        canViewCatalogProduct,
        canEditCatalogProduct,
        canDeleteCatalogProduct,

        canUpdateActionRequestState,

        ...withAuroraPolicies()
      }}
    >
      {children}
    </PolicyContext.Provider>
  )
}
